
import { firstValueFrom, of as observableOf, Subject } from 'rxjs'

import SearchIcon from '@mui/icons-material/Search'
import WarningIcon from '@mui/icons-material/Warning'
import Backdrop from '@mui/material/Backdrop'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Input from '@mui/material/Input'
import { PPDialog } from 'components/PPDialog'
import AdminPage from 'admin/components/AdminPage'
import { impersonateUser, stopImpersonateUser } from 'admin/services/actions'
import { searchApps } from 'admin/services/apps/net'
import { impersonationSelector } from 'admin/services/selectors'
import {
  clearFBTokens,
  clearUserData,
  getIntercomData,
  getUserData,
  makeAdmin,
  resetPassword,
  restartTrial,
  toggleBlockApp,
  toggleUserBlocked
} from 'admin/services/users/actions'
import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router'
import { catchError, mergeMap, tap } from 'rxjs/operators'
import { message } from 'services/snackbar'
import { StoreThunkDispatch } from 'store/state'
import styles from './AppsRoute.pcss'
import { AppInfo, AppTableRow } from './AppTableRow'

export function AppsRoute() {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const impersonate = useSelector(impersonationSelector)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const appId = searchParams.get('appId')
  const [apps, setApps] = React.useState<AppInfo[]>([])
  const [loading, setLoading] = React.useState(false)
  const [intercomData, setIntercomData] = React.useState<any>(null)
  const [userData, setUserData] = React.useState<any>(null)
  const fetch$ = React.useRef<Subject<string>>(undefined)
  const searchInputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    fetch$.current = new Subject()
    fetch$.current
      .pipe(tap(() => {
        setLoading(true)
      })).pipe(
        mergeMap(id => dispatch(searchApps(id)).pipe(catchError(() => {
          return observableOf({ error: true })
        }))))
      .subscribe((response: any) => {
        setLoading(false)
        if (response.error) {
          dispatch(message('Error fetching apps!'))
        } else {
          setApps(response.map((a: any) => {
            return {
              ...a,
              isBeta: a.isBeta === 1 ? '1' : '0',
              masterAdmin: a.masterAdmin === 1 ? '1' : '0'
            }
          }))
        }
      })

    return () => fetch$.current?.unsubscribe()
  }, [dispatch])


  React.useEffect(() => {
    if (appId) {
      fetch$.current?.next(appId)
      navigate('/admin/support/apps', { replace: true })
    }
  }, [appId, navigate])

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const id = (e.target as HTMLInputElement).value.trim()
    if (e.key === 'Enter') {
      setApps([])
      fetch$.current?.next(id)
    }
  }

  const closeUserDataDialog = () => {
    setIntercomData(null)
    setUserData(null)
  }

  const onSelectApp = (app: AppInfo) => {
    navigate(`/admin/support/apps/${app.id}`, { state: { app } })
  }

  const promptDeleteUserData = (userId: string) => {
    firstValueFrom(dispatch(getUserData(userId)))
      .then(response => {
        if (response.success) {
          setUserData({ ...response.result, userId })
        } else {
          dispatch(message('Error occurred: ' + response.error?.message))
        }
      }).catch(error => {
        dispatch(message('Error occurred: ' + error))
      })
  }

  const genericErrorHandler = (error: Error | any) => {
    dispatch(message(`An error occurred: ${error.response?.error?.message || error.message}`))
    return false
  }

  const clearFacebookTokens = (userId: string) => {
    const confirmed = confirm('Are you sure?')
    if (confirmed) {
      firstValueFrom(dispatch(clearFBTokens(userId)))
        .then(() => {
          dispatch(message('Tokens cleared.'))
        })
        .catch(genericErrorHandler)
    }
  }

  const showUserData = (userId: string) => {
    firstValueFrom(dispatch(getIntercomData(userId))).then(setIntercomData).catch(genericErrorHandler)
  }

  const deleteUserData = () => {
    if (!userData?.userId) {
      closeUserDataDialog()
      return
    }
    firstValueFrom(dispatch(clearUserData(userData.userId)))
      .then(response => {
        if (response.success) {
          dispatch(message('User data deleted!'))
        } else {
          dispatch(message('Error occurred: ' + response.error?.message))
        }
        closeUserDataDialog()
      })
      .catch(error => {
        dispatch(message('Error occurred: ' + error.message))
        closeUserDataDialog()
      })
  }

  const onToggleUserBlocked = (userId: string) => {
    firstValueFrom(dispatch(toggleUserBlocked(userId)))
      .then(() => {
        dispatch(message('Success!'))
        const search = searchInputRef.current?.value
        if (search) {
          fetch$.current?.next(search)
        }
      })
      .catch(genericErrorHandler)
  }

  const onBlockApp = (id: string) => {
    firstValueFrom(dispatch(toggleBlockApp(id)))
      .then((response) => {
        if (response.error) {
          dispatch(message(`An error occurred: ${response.error}`))
          return
        }
        dispatch(message(`App ${response.text.toLowerCase().indexOf('unblock') !== -1 ? 'blocked' : 'unblocked'}!`))
        const search = searchInputRef.current?.value
        if (search) {
          fetch$.current?.next(search)
        }
      })
      .catch(genericErrorHandler)
  }

  const onRestartTrial = (appId: string) => {
    firstValueFrom(dispatch(restartTrial(appId)))
      .then(() => {
        dispatch(message('Trial restarted!'))
      })
      .catch((e: any) => {
        dispatch(message(`An error occurred: ${e.response?.error?.message}`))
      })
  }

  const onResetPassword = (userId: string) => {
    firstValueFrom(dispatch(resetPassword(userId)))
      .then(() => {
        dispatch(message('Password reset!'))
      })
      .catch(genericErrorHandler)
  }

  const onMakeMasterAdmin = async (userId: string, appId: string) => {
    const confirmed = confirm(`Do you want to make the user a master admin of app ${appId}?`)
    if (confirmed) {
      const makeAdminResult = await firstValueFrom(dispatch(makeAdmin(userId)))
        .then(() => {
          dispatch(message('Success!'))
          return true
        })
        .catch(genericErrorHandler)

      if (makeAdminResult !== undefined && makeAdminResult === true) {
        fetch$.current?.next(appId)
      }
    }
  }

  const onImpersonate = (user: any) => {
    dispatch(impersonateUser(user))
  }

  const onStopImpersonation = () => {
    dispatch(stopImpersonateUser())
  }

  return (
    <AdminPage title="Apps">
      {impersonate && (
        <div className={styles.current}>
          <p>
            <WarningIcon className={styles['icon-warn']} />
            {`You are currently impersonating user ${impersonate.userId} - ${impersonate.name}`}
            <Button onClick={onStopImpersonation} className={styles['btn-stop-imp']}>Stop</Button>
          </p>
        </div>
      )}
      <section className={styles.container}>
        <header className={styles.header}>
          <Input
            ref={searchInputRef}
            startAdornment={<SearchIcon />}
            placeholder="Search Apps by ID"
            classes={{ root: styles['search-input'] }}
            onKeyDown={onKeyDown}
          />
        </header>
        <div className={styles.content}>
          <table className={styles.table}>
            <thead className={styles.thead}>
              <tr className={styles.tr}>
                <td className={styles['cell-s']}>App ID</td>
                <td className={styles['cell-s']}>User ID</td>
                <td className={styles['cell-s']}>Stripe ID</td>
                <td className={styles['cell-s']}>Tier</td>
                <td className={styles['cell-s']}>Currency</td>
                <td>Email</td>
                <td>Name</td>
                <td className={styles['cell-s']}>Data</td>
                <td className={styles['cell-s']}>Posts</td>
                <td>FB Token</td>
                <td className={styles['cell-s']}>Master Admin</td>
                <td>Impersonate</td>
                <td className={styles['cell-s']}>Actions</td>
              </tr>
            </thead>
            <tbody className={styles.tbody}>
              {apps.map((app, i) => (
                <AppTableRow
                  key={`${app.userId}-${i}`}
                  app={app}
                  onClick={onSelectApp}
                  onClearFBTokens={clearFacebookTokens}
                  clearUserData={promptDeleteUserData}
                  makeMasterAdmin={onMakeMasterAdmin}
                  resetPassword={onResetPassword}
                  restartTrial={onRestartTrial}
                  toggleUserBlocked={onToggleUserBlocked}
                  onViewData={showUserData}
                  onImpersonate={onImpersonate}
                  toggleBlockApp={onBlockApp}
                />
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <Backdrop open={loading} className={styles.backdrop}>
        <CircularProgress color="primary" />
      </Backdrop>
      <PPDialog
        open={Boolean(intercomData)}
        onClose={closeUserDataDialog}
        title="User data"
        className={styles['dialog-paper']}
      >
        <div className={styles['dialog-content']}>
          {intercomData && [
            <div className={styles['dialog-left']}>
              <h3>Intercom Data</h3>
              <pre>
                {JSON.stringify(intercomData.intercomUser, null, 2)}
              </pre>
            </div>,
            <div className={styles['dialog-right']}>
              <h3>Database User Data</h3>
              <pre>
                {JSON.stringify(intercomData.databaseUser, null, 2)}
              </pre>
            </div>
          ]}
        </div>
      </PPDialog>
      <PPDialog
        open={Boolean(userData)}
        onClose={closeUserDataDialog}
        title="User data to be deleted"
        footer={
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={deleteUserData}
          >
            Delete data
          </Button>
        }
      >
        <div className={styles['dialog-content']}>
          {userData && (
            <pre>
              {JSON.stringify(userData, null, 2)}
            </pre>
          )}
        </div>
      </PPDialog>
    </AdminPage>
  )
}

export default AppsRoute
